import styled from '@emotion/styled'
import { getQueryLink } from '@pubstack/common/src/alert-utils'
import { Alert } from '@pubstack/common/src/analytics/check'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { formatToDateWithoutTime, parseFromDateWithoutTime } from '@pubstack/common/src/date'
import { compareNames } from '@pubstack/common/src/sort'
import { useAtom } from 'jotai'
import { DateTime } from 'luxon'
import { FunctionComponent, useEffect } from 'react'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { useAlerts } from '~/api/app/alerts.api'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { Icon } from '~/components/Icon'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { PageContent } from '~/components/PageContent'
import Table, { Column } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Widget } from '~/components/Widget'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useScopeCurrency } from '~/hooks/useScopeCurrency.hooks'
import { contextState } from '~/state'
import { WithClassName } from '~/types/utils'
import { displayWithCurrency } from '~/utils/string'
import { useAcknowledgeAlerts } from './hooks/useAlerts'

type AlertRedirectToAnalyticsRouteParams = {
  from: string
  to: string
  mapping?: string
  ctx?: string
}

/**
 * `center` was interpolating `css` from `@emotion/css`, which returns a *class name* rather than
 * serialised styles. Dropped into a `styled` template body that yields a bare `css-1a2b3c` where a
 * declaration belongs, which the parser discards — so the flex row was never applied at all. Writing
 * the two declarations plainly makes the prop finally do what it says, which is a real (small) visual
 * change on the one row that uses it: the arrow icon and its percentage become a centred flex row
 * instead of default block flow.
 *
 * Exported for the test that pins this down: the page needs live alert data to render the row at all,
 * so the assertion is the only thing standing between the fix and a silent regression back to a
 * declaration the parser throws away.
 */
export const RedAlert = styled.div<{ center?: boolean }>`
  ${Fonts.colors.Alert}
  font-weight: bold;
  ${({ center }) =>
    center &&
    `
      display: flex;
      align-items: center;
    `}
`

/** The keys in the order the row shows them, left to right. */
const ALERT_KEY_COLUMNS = ['tagId', 'bidder', 'adUnit'] as const

/**
 * The newest alert first, and two raised at the same time by what their row reads, left to right. The keys are
 * objects, so the string to compare is built from their labels, then their values.
 */
export const byNewestThenKeys = (a: Alert, b: Alert): number => {
  const keyOf = ({ keys }: Alert) => [...ALERT_KEY_COLUMNS.map((column) => keys[column]?.label ?? ''), ...ALERT_KEY_COLUMNS.map((column) => keys[column]?.value ?? '')].join('|')
  return b.time - a.time || compareNames(keyOf(a), keyOf(b))
}

type PureAlertsTableProps = WithClassName & {
  currencySymbol: CurrencySymbol
  data?: Alert[]
  loading: boolean
  onRowClick: (alert: Alert) => void
}

const PureAlertsTable: FunctionComponent<PureAlertsTableProps> = ({ currencySymbol, data, loading, onRowClick }) => {
  const columns: Column[] = [
    {
      name: 'Site',
      isSortable: false,
    },
    {
      name: 'Bidder',
      isSortable: false,
    },
    {
      name: 'Ad unit',
      isSortable: false,
    },
    {
      name: currencySymbol + ' (vs D-1)',
      isSortable: false,
    },
    {
      name: '% (vs D-1)',
      isSortable: false,
    },
    {
      name: 'Occurrence',
      isSortable: false,
    },
  ]
  const alerts = [...(data || [])].sort(byNewestThenKeys)
  return (
    <Table isLoading={loading} columns={columns}>
      {alerts.map((alert, index) => (
        <TableRow key={index} clickable={{ onClick: () => onRowClick(alert), trackingLabel: 'ana-alerts-bidders-detail-navigate' }}>
          <TableCell>{alert.keys.tagId ? alert.keys.tagId.label : '-'}</TableCell>
          <TableCell>{alert.keys.bidder ? alert.keys.bidder.label : '-'}</TableCell>
          <TableCell>{alert.keys.adUnit ? alert.keys.adUnit.label : '-'}</TableCell>
          <TableCell>
            <RedAlert>{'- ' + displayWithCurrency(Math.abs(Math.round(alert.comparedValue) - Math.round(alert.refValue)), currencySymbol)}</RedAlert>
          </TableCell>
          <TableCell>
            <RedAlert center>
              <Icon name={'arrow_bottom_right'} width={'16px'} fill={Colors.Alert} />
              {-Math.trunc(Math.abs(alert.value) * 100) + ' %'}
            </RedAlert>
          </TableCell>
          {/*As the alert in trigger as J for J-1 we need to minus 1 day from the alert time */}
          <TableCell>{DateTime.fromSeconds(alert.time).minus({ days: 1 }).toFormat('yyyy-MM-dd')}</TableCell>
        </TableRow>
      ))}
    </Table>
  )
}

export const AlertsPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const currencySymbol = useScopeCurrency()
  const [context, setContext] = useAtom(contextState)
  const { data: alerts, isFetching, isError, refetch } = useAlerts()
  const acknowledge = useAcknowledgeAlerts()
  const breadcrumbs = useBreadcrumbs()

  const redirect = (alert: Alert) => {
    // if bidder redirect on the bidder dashboard
    // otherwise redirect on overview dashboard
    let pathname: string
    let params: AlertRedirectToAnalyticsRouteParams
    if (alert.check.split('.')[0] === 'bidder') {
      pathname = `/analytics/bidders/${alert.keys.bidder?.label ?? ''}`
      params = {
        from: formatToDateWithoutTime(DateTime.fromSeconds(alert.time).minus({ days: 4 })),
        to: formatToDateWithoutTime(DateTime.fromSeconds(alert.time).minus({ days: 3 })),
        mapping: btoa(
          JSON.stringify({
            bidder: alert.keys.bidder?.value,
          })
        ),
        ...getQueryLink(alert, btoa),
      }
    } else {
      pathname = '/analytics/overview'
      params = {
        from: formatToDateWithoutTime(DateTime.fromSeconds(alert.time).minus({ days: 3 })),
        to: formatToDateWithoutTime(DateTime.fromSeconds(alert.time).minus({ days: 1 })),
        ...getQueryLink(alert, btoa),
      }
    }

    //TODO cfo 2022-08-03 - find a better solution to sync url to context globally
    setContext({
      timeRange: {
        from: parseFromDateWithoutTime(params.from),
        to: parseFromDateWithoutTime(params.to),
        tz: context.timeRange.tz,
      },
      filters: params['ctx'] ? JSON.parse(decodeURIComponent(atob(params['ctx']))) : {},
    })
    navigate({
      pathname,
      search: `?${createSearchParams(params)}`,
    })
  }

  useEffect(() => {
    acknowledge()
  }, [alerts, acknowledge])

  return (
    <PageContent
      breadcrumbs={breadcrumbs}
      headerActions={
        <Button variant={'tertiary'} iconName={'mail'} onClick={() => navigate('/settings/profile')} trackingLabel={'ana-alerts-settings-profile-navigate'}>
          Manage email subscription
        </Button>
      }
    >
      <Widget
        isLoading={isFetching}
        error={isError}
        empty={false}
        icon={'notification'}
        title={'Summary'}
        onRefreshClick={() => {
          refetch()
        }}
        refreshTrackingLabel={'ana-alerts-refresh'}
      >
        {!isFetching && (alerts || []).length === 0 ? (
          <IllustrationMessage iconName={'binoculars'} iconSize={'190px'} message={"Nothing to see, you're all good !"} />
        ) : (
          <PureAlertsTable currencySymbol={currencySymbol} loading={isFetching} data={alerts} onRowClick={redirect} />
        )}
      </Widget>
    </PageContent>
  )
}
